import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Chip,
} from "@mui/material";
import { IconEye, IconPencil, IconX } from "@tabler/icons-react";
import GridComponent from "../../shared-components/gridComponent";
import SnackState from "../../shared-components/snackbar/snackState";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import MarketersModal from "./MarketersModal";
import urlConstant from "../../constants/urlConstants";
import { trackPromise } from "react-promise-tracker";
import apiClient from "../../lib/api";
import dataConstant from "../../constants/dataConstants";
import { selectUserDetail } from "../../redux/slice/userSlice";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

const MarketersList = () => {
  const [gridKey, setGridKey] = useState(false);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const [openPopup, setOpenPopup] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const refreshGrid = () => setGridKey((prev) => !prev);
  const [dialogMode, setDialogMode] = useState("");
  const getUserdetail = useSelector(selectUserDetail);

  const columns = [
    {
      field: "code",
      headerName: "Code",
      flex: 1,
      sortable: true,
    },
    {
      field: "fullName",
      headerName: "Name",
      flex: 1.5,
      sortable: true,
    },
    {
      field: "phone",
      headerName: "Phone",
      flex: 1.5,
      sortable: true,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 2,
      sortable: true,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.row.status}
          size="small"
          color={params.row.status === "Active" ? "success" : "error"}
        />
      ),
    },
    {
      field: "submitted",
      headerName: "Submitted",
      flex: 1,
      sortable: false,
    },
    {
      field: "approved",
      headerName: "Approved",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Link
          to={`/wellskyclients`}
          state={{
            status: "isForApproved",
            marketercode: `${params.row.code}`,
          }}
        >
          {params.value}
        </Link>
      ),
    },
    {
      field: "pending",
      headerName: "Pending",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Link
          to={`/submission`}
          state={{
            status: "isForPending",
            marketercode: `${params.row.code}`,
          }}
        >
          {params.value}
        </Link>
      ),
    },
  ];

  const getGridData = async ({ page, pageSize, sortModel, searchText }) => {
    const sort =
      sortModel.length > 0
        ? {
            sortColumnName: sortModel[0].field,
            sortOrder: sortModel[0].sort === "asc" ? "asc" : "desc",
          }
        : {
            sortColumnName: "",
            sortOrder: "",
          };

    const requestObject = {
      pageNumber: page,
      rowsPerPage: pageSize,
      searchColumns: {
        searchedColumn: "",
        searchText: "",
        columnDataType: "",
      },
      sortColumns: sort,
      searchText: searchText,
    };

    try {
      const response = await trackPromise(
        apiClient.post(urlConstant.Marketer.GetAllMarketers, requestObject, {
          authRequired: true,
        })
      );

      if (response?.status !== dataConstant.APIStatus.Success) {
        showSnackbar(response?.message || "Failed to fetch data", "error");
        return { items: [], totalCount: 0 };
      }

      const mappedData = response.data?.list?.map((item) => ({
        ...item,
        id: item.unqGuid,
      }));

      return {
        items: mappedData || [],
        totalCount: response.data?.totalCount || 0,
      };
    } catch (error) {
      showSnackbar("Error fetching data", "error");
      return { items: [], totalCount: 0 };
    }
  };

  const handleDelete = (row) => {
    setDeleteRow(row);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    deleteMarketer();
    setDeleteDialogOpen(false);
    setDeleteRow(null);
  };

  const handleAdd = () => {
    setDialogMode("add");
    setSelectedRow(null);
    setOpenPopup(true);
  };

  const handleEdit = (row) => {
    setDialogMode("edit");
    setSelectedRow(row);
    setOpenPopup(true);
  };

  const handleView = (row) => {
    setDialogMode("view");
    setSelectedRow(row);
    setOpenPopup(true);
  };

  const addMarketer = (newData) => {
    const payload = {
      code: newData.code,
      firstName: newData.firstName,
      lastName: newData.lastName,
      email: newData.email,
      cityUnqGuid: newData.cityId,
      userId: getUserdetail?.userId,
      phone: newData.phone,
    };

    trackPromise(
      apiClient
        .post(urlConstant.Marketer.CreateMarketers, payload, {
          authRequired: true,
        })
        .then((response) => {
          if (response?.status === "Success" && response?.statusCode === 200) {
            showSnackbar(response?.message, "success");
            refreshGrid();
            setOpenPopup(false);
          } else {
            showSnackbar(response?.message, "error");
          }
        })
        .catch((err) => {
          showSnackbar(err.message, "error");
        })
    );
  };

  const updateMarketer = (data) => {
    const payload = {
      UnqGuid: selectedRow.id,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      email: data.email,
      cityUnqGuid: data.cityId,
      userId: getUserdetail?.userId,
      status: data.status,
    };

    trackPromise(
      apiClient
        .put(urlConstant.Marketer.UpdateMarketers, payload, {
          authRequired: true,
        })
        .then((response) => {
          if (response?.status === "Success" && response?.statusCode === 200) {
            showSnackbar(response?.message, "success");
            refreshGrid();
            setOpenPopup(false);
          } else {
            showSnackbar(response?.message, "error");
          }
        })
        .catch((err) => {
          showSnackbar(err.message, "error");
        })
    );
  };

  // Soft Delete Marketer
  const deleteMarketer = () => {
    if (!deleteRow.id) {
      showSnackbar("Failed to delete marketer", "error");
      return;
    }

    const deletePayload = {
      marketerUnqGuid: deleteRow.id,
      userId: getUserdetail?.userId,
    };

    trackPromise(
      apiClient
        .delete(urlConstant.Marketer.DeleteMarketers, {
          data: deletePayload,
          authRequired: true,
        })
        .then((response) => {
          if (response?.status === "Success" && response?.statusCode === 200) {
            showSnackbar(response?.message, "success");
            refreshGrid();
          } else {
            showSnackbar(response?.message, "error");
          }
        })
        .catch((err) => {
          showSnackbar(err.message, "error");
        })
    );
  };

  const handleSave = (data) => {
    if (selectedRow) {
      updateMarketer(data); // Edit existing
    } else {
      addMarketer(data);
    }
    refreshGrid();
  };

  return (
    <>
      <GridComponent
        columns={columns}
        getGridData={getGridData}
        gridKey={gridKey}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
      >
        <DialogContent>
          <DialogContentText
            typography="h5"
            color="default.main"
            textAlign="center"
          >
            Are you sure you want to delete{" "}
            <strong>{deleteRow?.fullName}</strong>?
          </DialogContentText>
          <DialogActions
            sx={{ padding: 0, justifyContent: "center", mt: 4, border: "none" }}
          >
            <Button
              size="small"
              onClick={confirmDelete}
              color="primary"
              variant="contained"
            >
              Yes
            </Button>
            <Button
              size="small"
              color="primary"
              variant="outlined"
              onClick={() => setDeleteDialogOpen(false)}
            >
              No
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} />
    </>
  );
};

export default MarketersList;
